// Layer 3 (Transformation): polish.P9 (#657) — the GM's Value tab, C's
// per-metal value in the locked comp (`metal-accounting.html`, `valueTable`):
// one row per metal stream with its tonnes and NSR, and the NSR total against
// budget as the tab's primary figure. It re-lays the GM financial summary the
// route already read (`personaCardView('gm', …)`, the cost seed's
// `costPeriod` for the selected window); no figure is new. The budget exists
// only at the total, so the per-row budget and variance dashes go (absence is
// not content). Pure — no React.

import type { VizTone } from '@/versions/v1/modules/_viz';
import type { PeriodView } from '@/versions/v1/modules/ingestion/MetalAccountingLayer.transform';
import { personaCardView } from './BalancePersonaCard.transform';

export type ValueRow = { id: string; metal: 'Pb' | 'Zn' | 'Ag'; stream: string; tonnes: string; nsr: string };

export type ValueTabView = {
  total: { nsr: string; budget: string; variance: string; tone: VizTone; basis: string };
  rows: readonly ValueRow[];
};

const METAL_OF: Readonly<Record<string, ValueRow['metal']>> = { Pb: 'Pb', Zn: 'Zn', Ag: 'Ag' };

/** `null` at shift: the cost seed carries no shift window (unmeasured). */
export function valueTabView(view: PeriodView): ValueTabView | null {
  const card = personaCardView('gm', view);
  if (card.kind !== 'gm') return null;
  const total = card.rows.find((row) => row.product === 'Total');
  if (!total) return null;
  const rows = card.rows
    .filter((row) => row !== total)
    .map((row) => ({ row, metal: METAL_OF[row.product.split(' ')[0] ?? ''] }))
    .filter((entry): entry is { row: (typeof card.rows)[number]; metal: ValueRow['metal'] } => entry.metal !== undefined)
    .map(({ row, metal }) => ({ id: `value-${metal.toLowerCase()}`, metal, stream: row.product, tonnes: row.tonnes, nsr: row.nsr }));
  return {
    total: {
      nsr: total.nsr,
      budget: total.budget,
      // The cost seed prints a hyphen-minus; the glass uses the true minus.
      variance: total.variance.replace(/^-/, '−'),
      tone: total.tone,
      basis: total.tonnes,
    },
    rows,
  };
}
